import React from 'react';

// Interfaces
interface Layer {
  LayerId: number;
  Color: string;
  PriorityInConfigValue: number;
}

interface LinkedItem {
  ProjectId?: number;
  Layers?: Layer[];
  Icon?: string;
  IsParent: boolean;
  Progress?: number;
  Finished: boolean;
  IsRedFlag: boolean;
  Title: string;
  ProjectName: string;
}

interface Reply {
  OriginalConversationId: number;
  LinkedItem: LinkedItem | null;
}

interface Config {
  weburl: string;
}

interface CorgeeFunctions {
  openInfoWindow?: (conv: { ConversationId: number }) => void;
  goToProject?: (e: React.MouseEvent, conv: { ProjectId?: number }) => void;
}

interface ItemReplyProps {
  conversationId: number;
  reply: Reply;
  api?: object;
  projectId?: number;
  config: Config;
  corgeeFunctions?: CorgeeFunctions;
  isConversationDetailsView?: boolean;
  isConvDetailsView?: boolean;
}

const ItemReply: React.FC<ItemReplyProps> = ({
  conversationId,
  reply,
  api: _api,
  projectId,
  config: _config,
  corgeeFunctions,
  isConversationDetailsView: _isConversationDetailsView,
  isConvDetailsView,
}) =>
{
  const handleOpenInfoWindow = (e: React.MouseEvent<HTMLDivElement>): void =>
  {
    e.preventDefault();
    // Dashboard hosts read conv.ConversationId, so pass an object rather than a bare id
    corgeeFunctions?.openInfoWindow?.({ ConversationId: reply.OriginalConversationId });
  };

  const handleOpenNewWindow = (e: React.MouseEvent<HTMLDivElement>): void =>
  {
    e.preventDefault();
    const url =
      window.location.origin +
      import.meta.env.BASE_URL +
      'Dashboard/ConversationDetails/#/' +
      reply.OriginalConversationId;
    window.open(url, '_blank');
  };

  if (!reply.LinkedItem) return null;

  const projectIdValue = reply.LinkedItem.ProjectId ?? projectId;
  const projectUrl = `${window.location.origin}/ProjectDetails/${projectIdValue}`;

  const handleGoToProject = (e: React.MouseEvent<HTMLAnchorElement>): void =>
  {
    if (!corgeeFunctions?.goToProject) return;
    e.preventDefault();
    corgeeFunctions.goToProject(e, { ProjectId: projectIdValue });
  };

  const sortedLayers = reply.LinkedItem.Layers
    ? [...reply.LinkedItem.Layers].sort((a, b) => a.PriorityInConfigValue - b.PriorityInConfigValue)
    : [];

  const iconClass = reply.LinkedItem.Icon
    ? `corgee_com_card-conversation-item-subcard-reply-type-icon identifier-icon ${reply.LinkedItem.Icon}`
    : 'corgee_com_card-conversation-item-subcard-reply-type-icon identifier-icon icon-infinite-icon';

  const titleClass = reply.LinkedItem.IsRedFlag
    ? 'corgee_com_card-conversation-item-subcard-reply-title red'
    : 'corgee_com_card-conversation-item-subcard-reply-title';

  const flagClass = reply.LinkedItem.IsRedFlag
    ? 'corgee_com_card-conversation-item-flag-icon corgee-icon-red-flag red'
    : 'corgee_com_card-conversation-item-flag-icon corgee-icon-red-flag';

  return (
    <div className="corgee_com_card-conversation-item-subcard-reply">
      <div className="corgee_com_card-conversation-item-subcard-reply-container">
        <div className="corgee_com_card-conversation-item-subcard-reply-left">
          <div className="corgee_com_card-conversation-item-subcard-reply-actions">
            <div className="corgee_com_card-conversation-item-subcard-reply-layers">
              {sortedLayers.map(layer => (
                <span
                  key={`rcli-${conversationId}-${layer.LayerId}`}
                  className="corgee_com_card-conversation-item-subcard-reply-layer"
                  style={{ backgroundColor: layer.Color }}
                />
              ))}
            </div>

            <span className={iconClass} />

            {reply.LinkedItem.IsParent ? (
              <span className="team-capsule">
                <span className="inner" style={{ width: `${reply.LinkedItem.Progress ?? 0}%` }} />
              </span>
            ) : (
              <div className="corgee_com_checkbox">
                <input
                  type="checkbox"
                  disabled
                  checked={reply.LinkedItem.Finished}
                  className="taskCardCheckbox"
                  readOnly
                />
                <label />
              </div>
            )}
          </div>

          <div className="corgee_com_card-conversation-item-subcard-reply-priority-controls">
            <div className={flagClass} />

            {!isConvDetailsView ? (
              <div
                className="corgee_com_card-conversation-item-subcard-reply-title-container"
                onClick={handleOpenInfoWindow}
                data-linked-conversationid={reply.OriginalConversationId}
              >
                <div className={titleClass}>{reply.LinkedItem.Title}</div>
              </div>
            ) : (
              <div
                className="corgee_com_card-conversation-item-subcard-reply-title-container"
                onClick={handleOpenNewWindow}
                data-linked-conversationid={reply.OriginalConversationId}
              >
                <div className={titleClass}>{reply.LinkedItem.Title}</div>
              </div>
            )}
          </div>
        </div>

        <div className="corgee_com_card-conversation-item-subcard-reply-right">
          <div className="corgee_com_card-conversation-item-subcard-reply-project-name-container">
            <a
              className="corgee_com_card-conversation-item-subcard-reply-project-name"
              href={projectUrl}
              onClick={handleGoToProject}
            >
              <span>{reply.LinkedItem.ProjectName}</span>
            </a>
          </div>
          <div className="title-card-action-menu" />
        </div>
      </div>
    </div>
  );
};

export default ItemReply;
